import { Typography } from "antd";
import type { ICellRendererParams } from "ag-grid-community";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteIcon from "../../../../assets/svg/bookingform/deleteIcon";
import Grid from "../../../atoms/Grid";
import { formatCurrency, formatDateByType } from "../../../libs/utils";
import { ButtonGroup } from "../../../molecules/Button/ButtonGroup";
import { Invoice } from "./types";

type SendInvoiceRow = Invoice & {
  tenantDetails?: { tenantNameEN?: string };
  _id?: string;
  unitCode?: string;
};

type SendInvoicesConfirmProps = {
  invoices: SendInvoiceRow[];
  onConfirm: (pdcIds: string[]) => void | Promise<unknown>;
  onClose: () => void;
  isLoading?: boolean;
};

const getRowId = (inv: SendInvoiceRow) => String(inv.pdcId ?? inv._id ?? "");

/** Fixed list area: same height for 0/1/50 rows; extra rows scroll inside grid. */
const GRID_HEADER_PX = 42;
const GRID_ROW_PX = 42;
const GRID_MAX_VISIBLE_ROWS = 8;
const GRID_FIXED_HEIGHT_PX = GRID_HEADER_PX + GRID_MAX_VISIBLE_ROWS * GRID_ROW_PX;

export const SendInvoicesConfirm = (props: SendInvoicesConfirmProps) => {
  const { invoices, onConfirm, onClose, isLoading } = props;

  const [rows, setRows] = useState<SendInvoiceRow[]>(() => invoices);

  useEffect(() => {
    setRows(invoices);
  }, [invoices]);

  const removeRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((r) => getRowId(r) !== rowId));
  }, []);

  const data = useMemo(
    () =>
      rows.map((inv) => ({
        rowId: getRowId(inv),
        invoiceNumber: inv.invoiceNumber ?? "",
        dueDate: inv.dueDate ?? "",
        propertyName: inv.propertyDetails?.propertyName ?? "",
        tenantName: inv.tenantDetails?.tenantNameEN ?? "",
        unitCode: inv.unitCode ?? inv.propertyDetails?.unitNumber ?? "",
        amount: inv.actualAmount,
      })),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "invoiceNumber",
        headerName: "Invoice no.",
        flex: 0.9,
        minWidth: 100,
        maxWidth: 124,
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        flex: 1,
        maxWidth: 140,
        valueFormatter: ({ value }) => formatDateByType(value),
      },
      { field: "propertyName", headerName: "Property", flex: 1, minWidth: 140 },
      { field: "tenantName", headerName: "Tenant Name", flex: 1.35, minWidth: 185 },
      { field: "unitCode", headerName: "Unit Code", flex: 1, maxWidth: 130 },
      {
        field: "amount",
        headerName: "Amount",
        flex: 1,
        minWidth: 150,
        maxWidth: 220,
        type: "rightAligned",
        valueFormatter: ({ value }) => formatCurrency(value),
        headerClass: "send-invoices-confirm__header-amount",
        cellClass: "send-invoices-confirm__cell-amount",
        cellStyle: { textAlign: "right" },
      },
      {
        colId: "remove",
        field: "rowId",
        headerName: "",
        width: 64,
        maxWidth: 64,
        minWidth: 64,
        flex: 0,
        sortable: false,
        resizable: false,
        suppressMenu: true,
        headerClass: "send-invoices-confirm__header-remove",
        cellClass: "send-invoices-confirm__cell-remove",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        // v28+: use cellRenderer for React; cellRendererFramework often fails to mount in modals
        cellRenderer: (params: ICellRendererParams) => {
          const rowId = params.data?.rowId as string | undefined;
          if (!rowId) return null;
          return (
            <button
              type="button"
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 6,
                margin: 0,
                lineHeight: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                removeRow(rowId);
              }}
              aria-label="Remove from send list"
            >
              <DeleteIcon style={{ margin: 0, display: "block" }} width={18} height={18} />
            </button>
          );
        },
      },
    ],
    [removeRow],
  );

  const handleConfirm = () => {
    const ids = rows.map((r) => getRowId(r)).filter(Boolean);
    void onConfirm(ids);
  };

  return (
    <div>
      {rows.length > 0 && (
        <p style={{ margin: 0, paddingTop: 10 }}>
          The {rows.length} invoice{rows.length === 1 ? "" : "s"} below will be sent to tenants. Remove any you do not
          want to include before confirming.
        </p>
      )}
      <div className="send-invoices-confirm-grid" style={{ height: GRID_FIXED_HEIGHT_PX }}>
        {rows.length === 0 ? (
          <div
            style={{
              height: GRID_FIXED_HEIGHT_PX,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 12px",
              textAlign: "center",
            }}
          >
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              No invoices left in this list. Cancel and select again, or close the dialog.
            </Typography.Paragraph>
          </div>
        ) : (
          <Grid
            className="pl-0 pr-0"
            columns={columns}
            data={data}
            height={GRID_FIXED_HEIGHT_PX}
            headerHeight={GRID_HEADER_PX}
            rowHeight={GRID_ROW_PX}
          />
        )}
      </div>
      <ButtonGroup
        style={{ marginTop: "2.5rem" }}
        primaryButton={{
          label: "Confirm and Send",
          onClick: handleConfirm,
          loading: isLoading,
          disabled: isLoading || rows.length === 0,
        }}
        secondaryButton={{
          label: "Cancel",
          onClick: onClose,
          disabled: isLoading,
        }}
      />
    </div>
  );
};
